const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// The iframe starts empty, so the app's own stylesheets have to come along or the receipt
// renders unstyled (its class names are hashed CSS-module names defined in those sheets).
// Link tags are rebuilt from the resolved `href` property because the raw attribute is relative
// and would resolve against the iframe's own URL.
const collectDocumentStyles = (): string => Array
  .from(document.querySelectorAll<HTMLElement>('style, link[rel="stylesheet"]'))
  .map((node) => (node instanceof HTMLLinkElement
    ? `<link rel="stylesheet" href="${escapeHtml(node.href)}" />`
    : node.outerHTML))
  .join('\n');

// The 310px receipt paper, in millimetres at the CSS 96dpi reference.
const PAPER_WIDTH_MM = 82;
const FALLBACK_PAGE_HEIGHT_MM = 297;

// `size` takes one or two lengths; mixing a length with `auto` is invalid and drops the whole
// declaration, so the page height is measured off the rendered paper instead.
const pageHeightMmOf = (paper: HTMLElement): number => {
  const pixels = paper.getBoundingClientRect().height;
  if (!Number.isFinite(pixels) || pixels <= 0) {
    return FALLBACK_PAGE_HEIGHT_MM;
  }
  return Math.ceil((pixels / 96) * 25.4) + 2;
};

export const buildSummaryReceiptDocument = (
  paperHtml: string,
  title: string,
  pageHeightMm: number = FALLBACK_PAGE_HEIGHT_MM,
): string => `<!DOCTYPE html>
<html dir="rtl" lang="he">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    ${collectDocumentStyles()}
    <style>
      @page { size: ${PAPER_WIDTH_MM}mm ${pageHeightMm}mm; margin: 0; }
      html, body { width: ${PAPER_WIDTH_MM}mm; margin: 0; padding: 0; background: #ffffff; }
      body > * { width: 100% !important; max-width: 100% !important; box-sizing: border-box; }
      /* An app stylesheet carries an unscoped print rule that hides every element on the page
         to isolate the accountant report; here the receipt IS the page, so undo it. */
      @media print {
        body, body * { visibility: visible !important; }
      }
    </style>
  </head>
  <body>${paperHtml}</body>
</html>`;

// Summary: Prints the rendered receipt paper through a hidden iframe so the browser's print
// dialog can save it as a PDF; the document title becomes the suggested filename.
export const printSummaryReceiptPdf = (paper: HTMLElement | null, title: string): void => {
  if (!paper) {
    return;
  }
  const html = buildSummaryReceiptDocument(paper.outerHTML, title, pageHeightMmOf(paper));
  const previousDocumentTitle = document.title;
  document.title = title;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';

  const cleanup = () => {
    document.title = previousDocumentTitle;
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  };

  const runPrint = () => {
    const frameWindow = iframe.contentWindow;
    if (!frameWindow) {
      cleanup();
      return;
    }
    try {
      frameWindow.focus();
      frameWindow.print();
    } finally {
      setTimeout(cleanup, 500);
    }
  };

  // srcdoc guarantees the load event fires after this markup is parsed; document.write races with
  // the iframe's initial about:blank load and can print an empty page.
  iframe.addEventListener('load', runPrint, { once: true });
  iframe.srcdoc = html;
  document.body.appendChild(iframe);
};
